import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WriteOnly } from '../components/ViewerGuard';
import { targetsApi, projectsApi, outreachApi, pipelineApi, formatApiError } from '../lib/api';
import { HelpButton } from '../components/HelpPanel';
import SearchableSelect from '../components/SearchableSelect';
import {
  Mail,
  Send,
  RefreshCw,
  Copy,
  Check,
  Loader2,
  ChevronDown,
  FileText,
  Clock,
  CheckCircle,
  Target,
  Sparkles,
  ArrowRight,
  Trash2
} from 'lucide-react';

const OUTREACH_TYPES = [
  { id: 'cold_intro', label: 'Cold Intro', description: 'First contact — lead with the sharpest credential.' },
  { id: 'warm_followup', label: 'Warm Follow-up', description: 'Reference a prior interaction and move forward.' },
  { id: 'meeting_request', label: 'Meeting Request', description: 'Direct ask for a call with a specific purpose.' },
  { id: 'post_meeting', label: 'Post-meeting Follow-up', description: 'Reference what was discussed, propose next steps.' },
  { id: 're_engagement', label: 'Re-engagement', description: 'Acknowledge the gap, bring something new.' },
];

import usePageTitle from '../hooks/usePageTitle';
import HowDoesThisWorkPill from '../components/HowDoesThisWorkPill';

const OutreachPage = () => {
  usePageTitle('Outreach Generator');
  const [searchParams] = useSearchParams();
  const prefillTargetId = searchParams.get('target_id') || '';
  const prefillProjectId = searchParams.get('project_id') || '';
  const prefillPipelineId = searchParams.get('pipeline_id') || '';
  const prefillEventContext = searchParams.get('event_context') || '';

  const [targets, setTargets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const [selectedTarget, setSelectedTarget] = useState(prefillTargetId);
  const [selectedProject, setSelectedProject] = useState(prefillProjectId);
  const [selectedType, setSelectedType] = useState(prefillEventContext ? 'meeting_request' : 'cold_intro');
  const [pipelineRecordId, setPipelineRecordId] = useState(prefillPipelineId);

  const [draftText, setDraftText] = useState('');
  const [draftMeta, setDraftMeta] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loggedAsSent, setLoggedAsSent] = useState(false);

  // Context-first panel state
  const [showContextPanel, setShowContextPanel] = useState(!!prefillEventContext);
  const [contextType, setContextType] = useState(prefillEventContext ? 'other' : 'email_thread');
  const [priorContext, setPriorContext] = useState(prefillEventContext);
  const [priorContextLoading, setPriorContextLoading] = useState(false);
  const [priorContextSource, setPriorContextSource] = useState(null);
  const [recordUpdates, setRecordUpdates] = useState(null);
  const contextPanelRef = useRef(null);

  // Saved drafts history
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [targetsRes, projectsRes] = await Promise.all([
          targetsApi.getAll(),
          projectsApi.getAll(),
        ]);
        setTargets(targetsRes.data);
        setProjects(projectsRes.data);
        if (prefillTargetId) setSelectedTarget(prefillTargetId);
        if (prefillProjectId) setSelectedProject(prefillProjectId);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [prefillTargetId, prefillProjectId]);

  // Load drafts when pipeline record is selected
  useEffect(() => {
    if (pipelineRecordId) {
      loadDrafts();
    }
  }, [pipelineRecordId]);

  const loadDrafts = async () => {
    if (!pipelineRecordId) return;
    setLoadingDrafts(true);
    try {
      const res = await outreachApi.getDrafts(pipelineRecordId);
      setSavedDrafts(res.data);
    } catch (err) {
      console.error('Failed to load drafts:', err);
    } finally {
      setLoadingDrafts(false);
    }
  };

  // When outreach type changes, show/hide context panel
  const handleTypeChange = async (type) => {
    setSelectedType(type);
    if (type !== 'cold_intro') {
      setShowContextPanel(true);
      // Auto-scroll to context panel after render
      setTimeout(() => {
        contextPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      // Fetch prior context if target is selected
      if (selectedTarget) {
        setPriorContextLoading(true);
        try {
          const res = await outreachApi.getPriorContext(selectedTarget);
          if (res.data.last_outreach) {
            setPriorContext(res.data.last_outreach.text);
            setPriorContextSource(`Using your last logged outreach as context (${res.data.last_outreach.type}, sent ${new Date(res.data.last_outreach.sent_at).toLocaleDateString()})`);
          } else {
            setPriorContextSource(null);
          }
        } catch {
          setPriorContextSource(null);
        } finally {
          setPriorContextLoading(false);
        }
      }
    } else {
      setShowContextPanel(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTarget || !selectedProject) {
      setError('Please select both a target and a project.');
      return;
    }
    setGenerating(true);
    setError('');
    setDraftText('');
    setDraftMeta(null);
    setRecordUpdates(null);
    setLoggedAsSent(false);
    setCopied(false);

    try {
      const contextTypeLabels = { email_thread: 'Email Thread', meeting_notes: 'Meeting Notes', call_transcript: 'Call Transcript', whatsapp: 'WhatsApp', other: 'Other' };
      const res = await outreachApi.generate(
        selectedTarget, selectedProject, selectedType,
        selectedType !== 'cold_intro' ? priorContext : null,
        selectedType !== 'cold_intro' ? (contextTypeLabels[contextType] || contextType) : null,
      );
      setDraftText(res.data.draft_text);
      setDraftMeta({
        target_name: res.data.target_name,
        project_name: res.data.project_name,
        outreach_type: res.data.outreach_type,
      });
      if (res.data.record_updates) {
        setRecordUpdates(res.data.record_updates);
      }
      setShowContextPanel(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draftText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = draftText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLogAsSent = async () => {
    if (!draftText.trim()) return;
    setSaving(true);
    setError('');
    try {
      // Save draft
      const saveRes = await outreachApi.saveDraft({
        pipeline_record_id: pipelineRecordId || 'standalone',
        outreach_type: selectedType,
        draft_text: draftText,
      });

      // Mark as sent
      await outreachApi.markSent(saveRes.data.id);

      // If we have a pipeline record, also log contact
      if (pipelineRecordId) {
        await pipelineApi.logContact(pipelineRecordId);
      }

      setLoggedAsSent(true);
      await loadDrafts();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!draftText.trim()) return;
    setSaving(true);
    setError('');
    try {
      await outreachApi.saveDraft({
        pipeline_record_id: pipelineRecordId || 'standalone',
        outreach_type: selectedType,
        draft_text: draftText,
      });
      await loadDrafts();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const getTargetName = (id) => targets.find(t => t.id === id)?.name || 'Unknown';
  const getProjectName = (id) => projects.find(p => p.id === id)?.name || id;
  const getOutreachLabel = (type) => OUTREACH_TYPES.find(t => t.id === type)?.label || type;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-[#00D4C8] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center">
            <Mail className="w-5 h-5 text-[#00D4C8]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="font-heading text-3xl text-white" data-testid="outreach-page-title">Outreach Generator</h1>
              <HelpButton />
              <HowDoesThisWorkPill feature="outreach" />
            </div>
            <p className="text-sm text-[#A3B5C7]/70">Generate tailored outreach messages — pre-loaded with target intelligence and written in your voice.</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400" data-testid="outreach-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Controls */}
        <div className="lg:col-span-1 space-y-6">
          <div className="card-surface p-6 space-y-5">
            <h3 className="text-sm uppercase tracking-[0.2em] text-[#00D4C8] font-medium">Configure</h3>

            {/* Target selector */}
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#A3B5C7] mb-2">Target</label>
              <SearchableSelect
                options={targets.map(t => ({ value: t.id, label: t.name, type: t.type || '' }))}
                value={selectedTarget}
                onChange={setSelectedTarget}
                placeholder="Search targets..."
                typeKey="type"
                filterTypes={['Foundation', 'Brand', 'Broadcaster', 'Technology', 'Individual', 'Other']}
                testId="outreach-target-select"
              />
              {selectedTarget && (
                <p className="text-xs text-[#A3B5C7] mt-1">
                  {targets.find(t => t.id === selectedTarget)?.type}
                  {targets.find(t => t.id === selectedTarget)?.bd_work_types?.length > 0 &&
                    ` — ${targets.find(t => t.id === selectedTarget).bd_work_types.join(', ')}`
                  }
                </p>
              )}
            </div>

            {/* Project selector */}
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#A3B5C7] mb-2">Project</label>
              <SearchableSelect
                options={projects.map(p => ({ value: p.id, label: p.name, type: p.category || '' }))}
                value={selectedProject}
                onChange={setSelectedProject}
                placeholder="Search projects..."
                typeKey="type"
                testId="outreach-project-select"
              />
            </div>

            {/* Outreach type */}
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#A3B5C7] mb-2">Outreach Type</label>
              <div className="grid grid-cols-2 gap-2">
                {OUTREACH_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleTypeChange(type.id)}
                    className={`text-left px-3 py-2.5 rounded-lg border transition-all ${
                      selectedType === type.id
                        ? 'bg-[#00D4C8] border-[#00D4C8] text-[#0A1628]'
                        : 'bg-[#0A1628] border-[#1A2F4C] text-[#A3B5C7] hover:border-[#00D4C8]/30 hover:text-white'
                    }`}
                    data-testid={`outreach-type-${type.id}`}
                  >
                    <p className="text-xs font-medium">{type.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Context Panel (for non-cold types) */}
            <AnimatePresence>
              {showContextPanel && selectedType !== 'cold_intro' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div ref={contextPanelRef} className="p-5 rounded-lg bg-[#0A1628] border border-[#00D4C8]/20" data-testid="context-panel">
                    <h3 className="text-sm font-medium text-white mb-1">Prior Context</h3>
                    <p className="text-xs text-[#A3B5C7] mb-3">
                      Paste the previous email, meeting notes, call transcript, or WhatsApp thread that this outreach is responding to.
                    </p>

                    {priorContextSource && (
                      <div className="mb-3 px-3 py-2 rounded bg-[#00D4C8]/5 border border-[#00D4C8]/20">
                        <p className="text-xs text-[#00D4C8]">{priorContextSource}</p>
                      </div>
                    )}

                    {/* Context type tabs */}
                    <div className="flex gap-1 mb-3">
                      {[
                        { id: 'email_thread', label: 'Email Thread' },
                        { id: 'meeting_notes', label: 'Meeting Notes' },
                        { id: 'call_transcript', label: 'Call Transcript' },
                        { id: 'whatsapp', label: 'WhatsApp' },
                        { id: 'other', label: 'Other' },
                      ].map(ct => (
                        <button
                          key={ct.id}
                          onClick={() => setContextType(ct.id)}
                          className={`px-3 py-1 text-xs rounded transition-colors ${
                            contextType === ct.id
                              ? 'bg-[#00D4C8] text-[#0A1628]'
                              : 'bg-[#0E1C33] text-[#A3B5C7] hover:text-white'
                          }`}
                          data-testid={`context-type-${ct.id}`}
                        >
                          {ct.label}
                        </button>
                      ))}
                    </div>

                    <textarea
                      value={priorContext}
                      onChange={e => setPriorContext(e.target.value)}
                      placeholder="Paste prior conversation here..."
                      className="input-field min-h-[120px] text-sm resize-y"
                      data-testid="prior-context-textarea"
                    />

                    <div className="flex justify-end gap-2 mt-3">
                      <button
                        onClick={() => { setShowContextPanel(false); setPriorContext(''); }}
                        className="btn-secondary text-xs px-4 py-1.5"
                        data-testid="skip-context-btn"
                      >
                        Skip
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={generating || !selectedTarget || !selectedProject}
              className="w-full btn-primary flex items-center justify-center gap-2 py-3"
              data-testid="generate-outreach-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Drafting...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                  Generate Draft
                </>
              )}
            </button>
          </div>
        </div>

        {/* Right: Draft output + History */}
        <div className="lg:col-span-2 space-y-6">
          {/* Draft output */}
          <AnimatePresence mode="wait">
            {(generating || draftText) && (
              <motion.div
                key="draft"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="card-surface p-6"
                data-testid="outreach-draft-container"
              >
                {/* Draft header */}
                {draftMeta && (
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-white font-medium">
                        {getOutreachLabel(draftMeta.outreach_type)} — {draftMeta.target_name}
                      </h3>
                      <p className="text-xs text-[#A3B5C7]">Re: {draftMeta.project_name}</p>
                    </div>
                    {loggedAsSent && (
                      <span className="flex items-center gap-1.5 px-3 py-1 text-xs bg-green-500/20 text-green-400 rounded-full border border-green-500/30">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Logged as Sent
                      </span>
                    )}
                  </div>
                )}

                {generating ? (
                  <div className="py-12 text-center">
                    <Loader2 className="w-8 h-8 text-[#00D4C8] animate-spin mx-auto mb-4" />
                    <p className="text-[#A3B5C7]">Claude is drafting your message...</p>
                    <p className="text-xs text-[#A3B5C7] mt-1">This may take 30-60 seconds</p>
                  </div>
                ) : (
                  <>
                    {/* Editable textarea */}
                    <textarea
                      value={draftText}
                      onChange={(e) => { setDraftText(e.target.value); setLoggedAsSent(false); }}
                      className="w-full bg-[#0A1628] border border-[#1A2F4C] rounded-lg p-4 text-[#D4E2F0] leading-relaxed resize-none focus:border-[#00D4C8] focus:ring-2 focus:ring-[#00D4C8]/20 transition-all"
                      rows={14}
                      data-testid="outreach-draft-text"
                    />

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-3 mt-4">
                      <button
                        onClick={handleCopy}
                        className="btn-secondary flex items-center gap-2 text-sm"
                        data-testid="copy-draft-btn"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied' : 'Copy'}
                      </button>

                      <button
                        onClick={handleSaveDraft}
                        disabled={saving}
                        className="btn-secondary flex items-center gap-2 text-sm"
                        data-testid="save-draft-btn"
                      >
                        <FileText className="w-4 h-4" />
                        Save Draft
                      </button>

                      <button
                        onClick={handleGenerate}
                        disabled={generating}
                        className="btn-secondary flex items-center gap-2 text-sm"
                        data-testid="regenerate-btn"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Regenerate
                      </button>

                      <button
                        onClick={handleLogAsSent}
                        disabled={saving || loggedAsSent}
                        className="btn-primary flex items-center gap-2 text-sm ml-auto"
                        data-testid="log-as-sent-btn"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : loggedAsSent ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        {loggedAsSent ? 'Sent' : 'Log as Sent'}
                      </button>
                    </div>

                    {/* Record Updates Summary */}
                    {recordUpdates && (
                      <div className="mt-4 p-4 rounded-lg bg-[#00D4C8]/5 border border-[#00D4C8]/20" data-testid="record-updates-summary">
                        <h4 className="text-xs uppercase tracking-wider text-[#00D4C8] font-medium mb-2">Target Record Updated</h4>
                        <p className="text-sm text-[#D4E2F0]">{recordUpdates.summary}</p>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty state */}
          {!generating && !draftText && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="card-surface p-12 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-[#00D4C8]/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#00D4C8]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl text-white mb-2">Ready to Draft</h3>
              <p className="text-[#A3B5C7] text-sm max-w-md mx-auto">
                Select a target and project, choose your outreach type, and hit Generate.
                Claude will write in James's voice — direct, no fluff, assumes the reader is smart.
              </p>
            </motion.div>
          )}

          {/* Saved Drafts History */}
          {savedDrafts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-surface p-6"
              data-testid="outreach-history"
            >
              <h3 className="text-sm uppercase tracking-[0.2em] text-[#A3B5C7] mb-4">Draft History</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {savedDrafts.map((draft) => (
                  <div
                    key={draft.id}
                    className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C] hover:border-[#00D4C8]/20 transition-colors cursor-pointer"
                    onClick={() => { setDraftText(draft.draft_text); setLoggedAsSent(draft.status === 'sent'); }}
                    data-testid={`draft-history-${draft.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-wide text-[#00D4C8]">
                        {getOutreachLabel(draft.outreach_type)}
                      </span>
                      <div className="flex items-center gap-2">
                        {draft.status === 'sent' ? (
                          <span className="flex items-center gap-1 text-xs text-green-400">
                            <CheckCircle className="w-3 h-3" /> Sent
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-[#A3B5C7]">
                            <FileText className="w-3 h-3" /> Draft
                          </span>
                        )}
                        <span className="text-xs text-[#A3B5C7]">{formatDate(draft.created_at)}</span>
                      </div>
                    </div>
                    <p className="text-sm text-[#D4E2F0] line-clamp-2">{draft.draft_text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OutreachPage;
